"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useGenerateZitadelToken } from "@/hooks/use-zitadel-tokens";
import { getEnv } from "@/lib/get_env";
import { formatDate } from "@/lib/utils";
import { useOrgStore, useUserStore } from "@/stores";
import { Check, Copy, Plus } from "lucide-react";
import React from "react";

export function ZitadelTokensView() {
  const user = useUserStore.use.user();
  const selectedOrgId = useOrgStore.use.selectedOrgId();
  const selectedOrg = useOrgStore.use.selectedOrg();
  const orgs = useOrgStore.use.orgs();

  const orgIdForQuery = user?.superAdmin ? selectedOrgId || undefined : selectedOrgId || user?.defaultOrg?.id;
  const generateTokenMutation = useGenerateZitadelToken();

  const [generatedTokenData, setGeneratedTokenData] = React.useState<{
    token: string;
    expirationDate: string;
  } | null>(null);
  const [copiedToken, setCopiedToken] = React.useState(false);
  const [copiedApiUrl, setCopiedApiUrl] = React.useState(false);

  const backendApiUrl = getEnv("VITE_BACKEND_SERVICE_URL");

  const handleGenerateToken = async () => {
    if (!orgIdForQuery) return;
    try {
      const result = await generateTokenMutation.mutateAsync(orgIdForQuery);
      if (result && result.token && result.details?.creation_date) {
        setGeneratedTokenData({
          token: result.token,
          expirationDate: result.details.creation_date,
        });
      }
    } catch (error) {
      console.error("Failed to generate token:", error);
    }
  };

  const copyToClipboard = async (text: string, setter: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const hasOrgAccess = user?.superAdmin || (user?.organizations && user.organizations.length > 0);

  if (!hasOrgAccess) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>You do not have permission to generate Organization Access Tokens.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Organization Access Tokens</h1>
          <p className="text-muted-foreground">
            Generate organization access tokens for authenticating with the Istari Digital CLI to download assets
          </p>
          {orgs.length > 1 && selectedOrg && (
            <div className="mt-2 flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                Viewing: {selectedOrg.name}
              </Badge>
            </div>
          )}
        </div>
        <Button onClick={handleGenerateToken} disabled={generateTokenMutation.isPending}>
          <Plus className="mr-2 h-4 w-4" />
          {generateTokenMutation.isPending ? "Generating..." : "Generate Token"}
        </Button>
      </div>

      {/* Backend API URL */}
      <Card>
        <CardHeader>
          <CardTitle>Backend API URL</CardTitle>
          <CardDescription>
            Use this URL with your organization access token for Istari Digital CLI authentication
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded bg-muted p-3 text-sm">{backendApiUrl}</code>
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(backendApiUrl || "", setCopiedApiUrl)}>
              {copiedApiUrl ? (
                <>
                  <Check className="mr-2 h-4 w-4 text-green-500" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Generated Token Banner */}
      {generatedTokenData && (
        <Card className="border-l-4 border-green-500 bg-green-50 dark:bg-green-950/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-green-900 dark:text-green-100">Token Generated Successfully!</CardTitle>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setGeneratedTokenData(null)}>
                ✕
              </Button>
            </div>
            <CardDescription className="text-green-800 dark:text-green-200">
              Copy this token now. You won't be able to see it again!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <code className="block flex-1 overflow-x-auto rounded bg-background p-3 font-mono text-sm">
                {generatedTokenData.token}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(generatedTokenData.token, setCopiedToken)}
              >
                {copiedToken ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            {generatedTokenData.expirationDate && (
              <p className="text-xs text-muted-foreground">Created: {formatDate(generatedTokenData.expirationDate)}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
